/**
 * The provenance drawer: an assertion, the observation behind it, and the captured
 * evidence.
 *
 * **Evidence is described, never rendered.** The metadata panel is text the API typed and
 * this page reads — including the captured URL, which is printed as text rather than
 * offered as a link, because a source URL is a subject-controlled destination and a
 * one-click navigation to it is a request this analyst did not choose to make.
 *
 * The API serves the body as `application/octet-stream`
 * with `Content-Disposition: attachment`, the client retypes the blob to the same thing
 * before handing it over, and the anchor carries `download` — because the platform
 * captures whatever a source returned, and a subject who does not want to be profiled can
 * return HTML containing script. There is no preview here, and the absence is the control.
 *
 * **This used to be a bare download anchor pointing at the API, and it never worked.** The
 * access token lives in memory, so an anchor navigation sent no credentials and the
 * refresh cookie is scoped to `/api/v1/auth`. Every click returned 401 — and with no error
 * path on an anchor, the browser saved the JSON error body under the name of the file the
 * analyst was trying to fetch. It looked like a successful download.
 */

import { useSearchParams } from 'react-router-dom';
import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, download, request } from '@/lib/api';
import type { AssertionResponse, EvidenceMetadata } from '@/lib/schemas';
import {
  Badge,
  Button,
  Card,
  ConfidenceBadge,
  EmptyState,
  ErrorState,
  Loading,
} from '@/components/ui';

/** The evidence drill-down for one case. */
export function EvidencePage({ caseId }: { caseId: string }): ReactElement {
  const [params] = useSearchParams();
  const assertionId = params.get('assertion');

  const assertion = useQuery({
    queryKey: ['assertion', assertionId],
    queryFn: () => request<AssertionResponse>(`/assertions/${assertionId}`),
    enabled: assertionId !== null,
  });

  if (!assertionId) {
    return <EmptyState>Open a claim from the results, graph or timeline to see its evidence.</EmptyState>;
  }
  if (assertion.isLoading) return <Loading />;
  if (assertion.error) return <ErrorState error={assertion.error} />;
  if (!assertion.data) return <EmptyState>That claim no longer exists.</EmptyState>;

  const item = assertion.data;
  const sha = item.provenance.evidence_sha256;

  return (
    <div className="space-y-4">
      <Card title="Claim">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="Predicate">
            <span className="font-mono text-xs">{item.predicate}</span>
          </Field>
          <Field label="Value">
            <span className="flex items-center gap-2">
              {item.object_value}
              {item.is_special_category && <Badge tone="warn">Art. 9</Badge>}
            </span>
          </Field>
          <Field label="Confidence">
            <ConfidenceBadge
              sourceReliability={item.confidence.source_reliability}
              informationCredibility={item.confidence.information_credibility}
              extractionConfidence={item.confidence.extraction_confidence}
            />
          </Field>
          <Field label="Observed">
            {item.provenance.observed_at.slice(0, 19).replace('T', ' ')}
          </Field>
        </dl>
      </Card>

      <Card title="Provenance">
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <Field label="Source">{item.provenance.plugin_name}</Field>
          <Field label="Plugin version">
            <span className="font-mono text-xs">{item.provenance.plugin_version}</span>
          </Field>
          <Field label="Extractor version">
            <span className="font-mono text-xs">{item.provenance.extractor_version}</span>
          </Field>
          <Field label="Source URL">
            <span className="break-all text-xs text-muted">{item.provenance.source_url}</span>
          </Field>
          <Field label="Evidence digest">
            <span className="break-all font-mono text-xs">{sha}</span>
          </Field>
          <Field label="Case">
            <span className="font-mono text-xs">{caseId.slice(0, 8)}</span>
          </Field>
        </dl>
      </Card>

      <Card title="Captured response">
        <p className="mb-3 text-xs text-muted">
          Served as a download, never rendered. The platform captures whatever a source
          returned, and rendering it in this origin would be stored cross-site scripting
          against whoever is reading the file.
        </p>
        {/* The digest is nullable: an assertion can outlive the blob it cited, which is
            what the retention tombstone design makes explicable. The old anchor
            interpolated `null` straight into the URL and produced a 404 shaped like a
            download. */}
        {sha !== null ? (
          <CapturedResponse sha={sha} />
        ) : (
          <p className="text-xs text-muted">
            The captured response has been deleted under the retention policy. The claim and
            its provenance remain; the bytes behind it do not.
          </p>
        )}
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactElement | string }): ReactElement {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

/**
 * What was captured, described — and then the bytes themselves.
 *
 * **The description is not decoration.** An analyst deciding whether to open a captured
 * response is deciding whether to open a file a subject controls, and until this panel
 * existed the only way to learn anything about it was to download it. The status the
 * source returned, whether the body was truncated at the size cap, and whether redaction
 * removed anything are all things you want to know *before* the file is on your disk.
 *
 * @param props - The evidence digest.
 * @returns The metadata panel and the download control.
 */
function CapturedResponse({ sha }: { sha: string }): ReactElement {
  const metadata = useQuery({
    queryKey: ['evidence-metadata', sha],
    queryFn: () => request<EvidenceMetadata>(`/evidence/${sha}/metadata`),
  });

  if (metadata.isLoading) return <Loading label="Reading metadata" />;

  // Metadata and content are separate endpoints, so one failing does not mean the other
  // will. The control stays; it reports its own failure if it has one.
  if (metadata.error || !metadata.data) {
    return (
      <div className="space-y-3">
        <ErrorState error={metadata.error ?? new Error('No metadata was returned.')} />
        <EvidenceDownload sha={sha} />
      </div>
    );
  }

  const item = metadata.data;

  return (
    <div className="space-y-3" data-testid="evidence-metadata">
      <dl className="grid gap-3 text-sm sm:grid-cols-2">
        <Field label="Captured">{item.captured_at.slice(0, 19).replace('T', ' ')}</Field>
        <Field label="Source">
          <span>
            {item.plugin.name} <span className="font-mono text-xs text-muted">{item.plugin.version}</span>
          </span>
        </Field>
        <Field label="Request">
          <span className="break-all text-xs">
            <span className="font-mono">{item.request.method}</span> {item.request.url}
          </span>
        </Field>
        <Field label="Response">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs">{item.response.status}</span>
            <span className="text-xs text-muted">{item.response.latency_ms} ms</span>
            {/* `Badge` forwards no `data-*`, so the test id goes on a wrapper. */}
            {item.response.truncated && (
              <span data-testid="evidence-truncated">
                <Badge tone="warn">Truncated</Badge>
              </span>
            )}
          </span>
        </Field>
        <Field label="Stored size">
          <span>
            {formatBytes(item.byte_size)}
            {item.compression !== 'none' && (
              <span className="ml-2 text-xs text-muted">{item.compression}</span>
            )}
          </span>
        </Field>
        <Field label="Media type">
          {/* Reported, never acted on: the body is always served as octet-stream. */}
          <span className="font-mono text-xs">{item.media_type}</span>
        </Field>
        <Field label="Redaction">
          {/* `false` does not mean redaction was skipped — it means nothing matched, and
              an analyst asking whether a blob is verbatim needs that distinction. */}
          <span className="text-xs">
            {item.redaction_applied ? 'Applied before hashing' : 'Nothing matched'}
          </span>
        </Field>
        <Field label="Digest">
          <span className="break-all font-mono text-xs">{sha}</span>
        </Field>
      </dl>

      {item.available ? (
        <EvidenceDownload sha={sha} />
      ) : (
        <p className="text-xs text-muted" data-testid="evidence-tombstoned">
          {/* Pre-empted rather than discovered by clicking: the endpoint answers 410 with
              this same reason, and offering a control that cannot work is worse than
              saying so. The metadata survives the body, which is the point of a
              tombstone — a dangling citation explains itself. */}
          The captured response has been revoked
          {item.tombstone_reason === null ? '' : `: ${item.tombstone_reason}`}. Its metadata
          is retained; the bytes are gone.
        </p>
      )}
    </div>
  );
}

/**
 * Render a byte count at human scale.
 *
 * @param bytes - The stored size.
 * @returns A short label.
 */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The download control for one captured response.
 *
 * A button rather than a link, because the request has to carry the bearer token and an
 * anchor cannot. The failure is shown rather than saved to disk.
 *
 * @param props - The evidence digest.
 * @returns The control.
 */
function EvidenceDownload({ sha }: { sha: string }): ReactElement {
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  async function onClick(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await download(`/evidence/${sha}/content`, `${sha}.bin`);
    } catch (caught) {
      if (!(caught instanceof ApiError)) throw caught;
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <Button
        tone="secondary"
        disabled={busy}
        data-testid="download-evidence"
        onClick={() => void onClick()}
      >
        {busy ? 'Fetching…' : '⬇ Download evidence'}
      </Button>
      {error !== null && <ErrorState error={error} />}
    </div>
  );
}
